import { useState } from "react";
import styled from "styled-components";
import { Campo } from "../../services/tipos";
import { TipoFormulario } from "../../services/constantes";
import Select, { components as ReactSelectComponents } from "react-select";
import Creatable from 'react-select/creatable';

interface SelectBoxSearchProps {
    campo: Campo;
    modo: string;
    isClearable?: boolean;
    apenasLeitura?: boolean;
    getFieldValue: (nome: string) => string;
    handleInputChange: (nome: string, value: string) => void;
    hint?: string;
    onBlur?: (e: any) => void;
    hasError?: boolean;
    allowNotFoundValues?: boolean;
}

const SelectBoxContainer = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
`;

const StyledSelect = styled(Select) <{ hasError?: boolean }>`
    width: 100%;
    height: 38px;
    font-size: 1rem;
    padding-left: 0px;

    &:disabled {
        background-color: #e9ecef;
    }

    .select-box-container & {
        border-radius: 0.375rem;
    }
`;

const StyledCreatableSelect = styled(Creatable) <{ hasError?: boolean }>`
    width: 100%;
    height: 38px;
    font-size: 1rem;
    padding-left: 0px;

    &:disabled {
        background-color: #e9ecef;
    }

    .select-box-container & {
        border-radius: 0.375rem;
    }
`;

function SelectBoxSearch(props: SelectBoxSearchProps) {
    const [opcaoFiltrada, setOpcoesFiltradas] = useState<string>("");
    const [inputValue, setInputValue] = useState("");

    const handleOpcaoFiltrada = (newValue: string) => {
        setOpcoesFiltradas(newValue);
        setInputValue(newValue);
    };

    const options = props.campo.opcoes?.map(opcao => ({
        value: opcao.valor,
        label: opcao.label,
    })) || [];

    const handleInputChangeLocal = (selectedOption: any, actionMeta: any) => {
        // Se estiver digitando, apenas atualiza o inputValue
        if (actionMeta.action === 'input-change') {
            return;
        }
        
        let value = selectedOption?.value ?? '';
        props.handleInputChange(props.campo.nome, value);
        props.campo.onCampoDigitar && props.campo.onCampoDigitar(value);
    };

    const handleBlur = () => {
        // Se allowNotFoundValues estiver ativado e houver texto digitado que não corresponde a nenhuma opção
        if (props.allowNotFoundValues && inputValue && !options.some(option => option.label === inputValue)) {
            const newOption = {
                value: inputValue,
                label: inputValue
            };
            
            props.handleInputChange(props.campo.nome, inputValue);
            props.campo.onCampoDigitar && props.campo.onCampoDigitar(inputValue);
            
            // Limpa o input após criar a nova opção
            setInputValue("");
            setOpcoesFiltradas("");
        }
        
        // Chama o onBlur original se existir
        const selectedValue = options.find(option => option.value == props.getFieldValue(props.campo.nome));
        props.campo.onCampoSair && props.campo.onCampoSair(selectedValue?.value);
        props.onBlur && props.onBlur(selectedValue?.value);
    };

    const currentValue = () => {
        const fieldValue = props.getFieldValue(props.campo.nome);
        
        // Se allowNotFoundValues estiver ativado e o valor atual não estiver nas opções
        if (props.allowNotFoundValues && fieldValue && !options.some(option => option.value === fieldValue)) {
            return {
                value: fieldValue,
                label: fieldValue
            };
        }
        
        return options.find(
            option => String(option.value).toLowerCase() === String(fieldValue).toLowerCase()
        ) ?? null;
    };

    return (
        <SelectBoxContainer>
            {
                props.allowNotFoundValues ? 
                <StyledCreatableSelect
                    onChange={handleInputChangeLocal}
                    options={options}
                    value={currentValue()}
                    onBlur={handleBlur}
                    isDisabled={(props.campo.apenasLeitura && props.campo.apenasLeitura == true) ?? props.modo === TipoFormulario.tpVisualizacao}
                    placeholder={props.campo.carregando ? "Carregando opções..." : (props.hint ? props.hint : "Selecione uma opção")}
                    required={props.campo.required}
                    inputValue={opcaoFiltrada}
                    onInputChange={handleOpcaoFiltrada}
                    components={{
                        Input: ReactSelectComponents.Input
                    }}
                    isClearable={props.isClearable}
                    getOptionLabel={(e: any) => e.label}
                    getOptionValue={(e: any) => String(e.value)}
                    noOptionsMessage={() => "Digite para criar uma nova opção"}
                    menuPortalTarget={document.body}
                    menuPlacement="auto"
                    hasError={props.hasError}
                    formatCreateLabel={(inputValue: any) => `Criar "${inputValue}"`}
                    styles={{
                        control: (base: any, state: any) => ({
                            ...base,
                            minHeight: '38px',
                            height: '38px',
                            padding: '0px',
                            border: `1px solid ${props.hasError ? '#dc3545' : '#ced4da'}`,
                            boxShadow: props.hasError ? '0 0 0 0.2rem rgba(220, 53, 69, 0.25)' : 'none',
                            '&:hover': {
                                borderColor: props.hasError ? '#dc3545' : '#ced4da'
                            },
                            transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out'
                        }),
                        indicatorsContainer: (base: any) => ({
                            ...base,
                            height: '38px',
                        }),
                        valueContainer: (base: any) => ({
                            ...base,
                            height: '38px',
                            padding: '0 8px',
                        }),
                        input: (base: any) => ({
                            ...base,
                            margin: 0,
                            padding: 0,
                        }),
                        menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
                    }}
                /> :
                <StyledSelect
                    onChange={handleInputChangeLocal}
                    options={options}
                    value={currentValue()}
                    onBlur={handleBlur}
                    isDisabled={(props.campo.apenasLeitura && props.campo.apenasLeitura == true) ?? props.modo === TipoFormulario.tpVisualizacao}
                    placeholder={props.campo.carregando ? "Carregando opções..." : (props.hint ? props.hint : "Selecione uma opção")}
                    required={props.campo.required}
                    inputValue={opcaoFiltrada}
                    onInputChange={handleOpcaoFiltrada}
                    components={{
                        Input: ReactSelectComponents.Input
                    }}
                    isClearable={props.isClearable}
                    getOptionLabel={(e: any) => e.label}
                    getOptionValue={(e: any) => String(e.value)}
                    noOptionsMessage={() => "Nenhuma opção encontrada"}
                    menuPortalTarget={document.body}
                    menuPlacement="auto"
                    hasError={props.hasError}
                    styles={{
                        control: (base: any, state: any) => ({
                            ...base,
                            minHeight: '38px',
                            height: '38px',
                            padding: '0px',
                            border: `1px solid ${props.hasError ? '#dc3545' : '#ced4da'}`,
                            boxShadow: props.hasError ? '0 0 0 0.2rem rgba(220, 53, 69, 0.25)' : 'none',
                            '&:hover': {
                                borderColor: props.hasError ? '#dc3545' : '#ced4da'
                            },
                            transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out'
                        }),
                        indicatorsContainer: (base: any) => ({
                            ...base,
                            height: '38px',
                        }),
                        valueContainer: (base: any) => ({
                            ...base,
                            height: '38px',
                            padding: '0 8px',
                        }),
                        input: (base: any) => ({
                            ...base,
                            margin: 0,
                            padding: 0,
                        }),
                        menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
                    }}
                />
            }
        </SelectBoxContainer>
    );
}

export default SelectBoxSearch;